/**
 * The glyphs.
 *
 * Every mark on the map is here. The rules they follow:
 *   - A host is a ring gauge: outer arc is CPU, inner arc is memory. You can
 *     read a host's pressure without selecting it.
 *   - A bridge is a narrow upright bar, not a disc, so a network segment never
 *     reads as a machine. Internet-facing bridges get the amber treatment.
 *   - A guest is a small disc coloured by run state, with an amber halo when it
 *     sits on an internet-facing bridge, and a square notch when it routes for
 *     a segment behind it.
 *   - A guest carrying snapshots gets a diamond badge in its upper right,
 *     amber for recent and red for one older than the stale threshold. It is
 *     drawn at every zoom, on purpose: a forgotten snapshot is a disk filling
 *     up, and you should not have to click a VM to find out it has one.
 *
 * The tree runs left to right, so every label is drawn to the *right* of its
 * glyph and vertically centred on it. Keep that consistent: mixed anchoring in
 * a horizontal tree reads as noise.
 */

import { formatBitRate, type SnapshotSeverity } from '@proxmon/shared';
import { edgeAddressText, type MapBridgeNode, type MapGuestNode, type MapHostNode } from './layout.ts';
import { ratio } from '../format.ts';

/** Keep in step with LABEL_GAP in layout.ts, which reserves the column width. */
const LABEL_GAP = 9;

const COLOR = {
  ok: '#3fb950',
  idle: '#57627a',
  warn: '#db8c26',
  danger: '#f0554a',
  edge: '#f5a524',
  accent: '#4c8dff',
  text: '#e4e9f2',
  track: '#232c3d',
} as const;

/** SVG arc from `start` to `start + sweep` radians, clockwise, stroke-only. */
function arcPath(cx: number, cy: number, r: number, start: number, sweep: number): string {
  const end = start + Math.min(sweep, Math.PI * 2 - 0.001);
  const x1 = cx + Math.cos(start) * r;
  const y1 = cy + Math.sin(start) * r;
  const x2 = cx + Math.cos(end) * r;
  const y2 = cy + Math.sin(end) * r;
  const large = end - start > Math.PI ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

function pressureColor(value: number | null): string {
  if (value === null) return COLOR.idle;
  if (value >= 0.9) return COLOR.danger;
  if (value >= 0.75) return COLOR.warn;
  return COLOR.accent;
}

interface GaugeProps {
  cx: number;
  cy: number;
  r: number;
  value: number | null;
  width: number;
  color?: string;
}

/** A ring gauge that starts at 12 o'clock. */
function Gauge({ cx, cy, r, value, width, color }: GaugeProps) {
  const start = -Math.PI / 2;
  const stroke = color ?? pressureColor(value);
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLOR.track} strokeWidth={width} />
      {value !== null && value > 0.005 && (
        <path
          d={arcPath(cx, cy, r, start, Math.PI * 2 * value)}
          fill="none"
          stroke={stroke}
          strokeWidth={width}
          strokeLinecap="round"
        />
      )}
    </>
  );
}

/**
 * The snapshot badge: a small diamond in the glyph's upper right.
 *
 * A diamond rather than the circle the host's ZFS warning uses, so the two
 * corner badges never read as the same thing at a glance, and outlined in the
 * page background so it stays legible over the disc it overlaps.
 */
function SnapshotBadge({ cx, cy, severity, size = 4.2 }: { cx: number; cy: number; severity: SnapshotSeverity; size?: number }) {
  if (severity === 'none') return null;
  const fill = severity === 'stale' ? COLOR.danger : COLOR.edge;
  return (
    <path
      d={`M ${cx} ${cy - size} L ${cx + size} ${cy} L ${cx} ${cy + size} L ${cx - size} ${cy} Z`}
      fill={fill}
      stroke="#0b0e14"
      strokeWidth={1.4}
      strokeLinejoin="round"
    />
  );
}

export interface GlyphState {
  selected: boolean;
  hovered: boolean;
  dimmed: boolean;
  /** hide small text at low zoom */
  showLabel: boolean;
  showDetail: boolean;
}

export function HostGlyph({ node, state }: { node: MapHostNode; state: GlyphState }) {
  const host = node.data;
  const cpu = host.cpu;
  const mem = ratio(host.memUsed, host.memTotal);
  const offline = !host.online;

  // Worst-case ZFS pool drives the little warning dot; free space is the thing
  // that silently kills a hypervisor at 03:00.
  const worstPool = host.zfs.reduce<number | null>((worst, pool) => {
    const used = ratio(pool.alloc, pool.size);
    if (used === null) return worst;
    return worst === null ? used : Math.max(worst, used);
  }, null);
  const poolDegraded = host.zfs.some((p) => p.health && p.health !== 'ONLINE');

  return (
    <g opacity={state.dimmed ? 0.24 : 1} className="hit">
      {(state.selected || state.hovered) && (
        <circle cx={node.x} cy={node.y} r={node.r + 12} fill="none" stroke={COLOR.accent} strokeWidth={state.selected ? 2 : 1} opacity={state.selected ? 0.9 : 0.45} />
      )}
      <circle cx={node.x} cy={node.y} r={node.r} fill="#141c2b" stroke={offline ? COLOR.danger : '#2f3a52'} strokeWidth={1.5} />
      <Gauge cx={node.x} cy={node.y} r={node.r - 4} value={offline ? null : cpu} width={3.5} />
      <Gauge cx={node.x} cy={node.y} r={node.r - 11} value={offline ? null : mem} width={3} color={mem !== null && mem >= 0.9 ? COLOR.danger : '#6f7fa6'} />

      {offline ? (
        <text x={node.x} y={node.y + 4} textAnchor="middle" fontSize={11} fill={COLOR.danger} fontWeight={700}>
          OFF
        </text>
      ) : (
        state.showDetail && (
          <text x={node.x} y={node.y + 4} textAnchor="middle" fontSize={11} fill={COLOR.text} fontFamily="ui-monospace, monospace">
            {cpu === null ? '—' : `${Math.round(cpu * 100)}%`}
          </text>
        )
      )}

      {(poolDegraded || (worstPool !== null && worstPool >= 0.9)) && (
        <circle
          cx={node.x + node.r * 0.72}
          cy={node.y - node.r * 0.72}
          r={5}
          fill={poolDegraded ? COLOR.danger : COLOR.warn}
          stroke="#0b0e14"
          strokeWidth={1.5}
        />
      )}

      {state.showLabel && (
        <text className="node-label left host" x={node.x + node.r + LABEL_GAP} y={node.y}>
          {node.label}
        </text>
      )}
    </g>
  );
}

/**
 * Colour for a saturation reading. Shares the host gauge's thresholds so a
 * busy bridge and a busy CPU look equally alarming, and adds a fourth step
 * above 100%: past capacity means the entered capacity is wrong, or the link
 * genuinely cannot keep up, and both deserve to shout.
 */
function saturationColor(value: number): string {
  if (value >= 1) return COLOR.danger;
  if (value >= 0.9) return COLOR.danger;
  if (value >= 0.75) return COLOR.warn;
  return COLOR.ok;
}

export function BridgeGlyph({ node, state }: { node: MapBridgeNode; state: GlyphState }) {
  const halfW = 8;
  const halfH = 14;
  const x = node.x - halfW;
  const y = node.y - halfH;
  const stroke = node.edge ? COLOR.edge : node.virtual ? '#39435c' : '#4a5876';
  const labelX = node.x + halfW + LABEL_GAP;

  const { traffic, saturation } = node.data;
  // Peak of the two directions, not their sum: the bar answers "how close is
  // the busiest direction to the limit", which is what saturation means on a
  // full-duplex link.
  const peakBitsPerSec = traffic ? Math.max(traffic.inBytesPerSec, traffic.outBytesPerSec) * 8 : null;

  // The bar fills the inside of the glyph, bottom-up, inset so the outline
  // stays readable at every zoom.
  const inset = 2;
  const trackH = halfH * 2 - inset * 2;
  const fillFraction = saturation === null ? 0 : Math.min(1, Math.max(0, saturation));
  const fillH = trackH * fillFraction;

  return (
    <g opacity={state.dimmed ? 0.24 : 1} className="hit">
      {(state.selected || state.hovered) && (
        <rect
          x={x - 6}
          y={y - 6}
          width={halfW * 2 + 12}
          height={halfH * 2 + 12}
          rx={9}
          fill="none"
          stroke={COLOR.accent}
          strokeWidth={state.selected ? 2 : 1}
          opacity={state.selected ? 0.9 : 0.45}
        />
      )}
      {node.edge && (
        <rect x={x - 3} y={y - 3} width={halfW * 2 + 6} height={halfH * 2 + 6} rx={8} fill="none" stroke={COLOR.edge} strokeWidth={1} opacity={0.3} />
      )}
      <rect
        x={x}
        y={y}
        width={halfW * 2}
        height={halfH * 2}
        rx={5}
        fill={node.edge ? '#2a2113' : '#182031'}
        stroke={stroke}
        strokeWidth={node.edge ? 1.6 : 1.2}
        strokeDasharray={node.virtual ? '3 3' : undefined}
      />

      {saturation !== null && fillH > 0.5 && (
        <rect
          x={x + inset}
          y={y + inset + (trackH - fillH)}
          width={halfW * 2 - inset * 2}
          height={fillH}
          rx={3}
          fill={saturationColor(saturation)}
          opacity={0.85}
        />
      )}

      {state.showLabel && (
        <text
          className={`node-label left bridge${node.edge ? ' edge' : ''}`}
          x={labelX}
          y={peakBitsPerSec === null ? node.y : node.y - 6}
        >
          {node.label}
          {state.showDetail && node.guestCount > 0 ? `  ${node.guestCount}` : ''}
        </text>
      )}

      {/* Throughput rides below the name rather than beside it: bridge names
          are already long, and a second line keeps the columns from colliding. */}
      {state.showDetail && peakBitsPerSec !== null && (
        <text className="node-label left bridge rate" x={labelX} y={node.y + 7}>
          {formatBitRate(peakBitsPerSec, 1)}
          {saturation !== null ? `  ${Math.round(saturation * 100)}%` : ''}
        </text>
      )}
    </g>
  );
}

export function GuestGlyph({ node, state }: { node: MapGuestNode; state: GlyphState }) {
  const guest = node.data;
  const running = guest.status === 'running';
  const fill = guest.template ? '#212a3c' : running ? '#16321f' : '#1a202c';
  const stroke = guest.template ? COLOR.idle : running ? COLOR.ok : COLOR.idle;
  const cpu = running ? guest.cpu : null;
  const routes = node.gatewayFor.length > 0;
  const labelX = node.x + node.r + LABEL_GAP;

  return (
    <g opacity={state.dimmed ? 0.2 : 1} className="hit">
      {(state.selected || state.hovered) && (
        <circle
          cx={node.x}
          cy={node.y}
          r={node.r + 9}
          fill="none"
          stroke={COLOR.accent}
          strokeWidth={state.selected ? 2 : 1}
          opacity={state.selected ? 0.9 : 0.45}
        />
      )}

      {/* The amber halo is the whole point of the map: it says "reachable from
          the internet" at any zoom level, without reading a single label. */}
      {node.edge && (
        <>
          <circle cx={node.x} cy={node.y} r={node.r + 5} fill="none" stroke={COLOR.edge} strokeWidth={1.4} opacity={0.55} />
          <circle cx={node.x} cy={node.y} r={node.r + 8} fill="none" stroke={COLOR.edge} strokeWidth={4} opacity={0.1} />
        </>
      )}

      <circle cx={node.x} cy={node.y} r={node.r} fill={fill} stroke={stroke} strokeWidth={1.4} />
      {cpu !== null && cpu > 0.02 && <Gauge cx={node.x} cy={node.y} r={node.r + 2.6} value={cpu} width={2} />}

      <SnapshotBadge
        cx={node.x + node.r * 0.76}
        cy={node.y - node.r * 0.76}
        severity={guest.snapshots.severity}
      />

      {/* A router gets a square core: it is a waypoint, not an endpoint. */}
      {routes ? (
        <rect x={node.x - 3.5} y={node.y - 3.5} width={7} height={7} rx={1} fill={stroke} opacity={0.95} />
      ) : guest.kind === 'lxc' ? (
        <rect x={node.x - 3} y={node.y - 3} width={6} height={6} rx={1.5} fill={stroke} opacity={0.85} />
      ) : (
        <circle cx={node.x} cy={node.y} r={2.8} fill={stroke} opacity={0.85} />
      )}

      {state.showLabel && (
        <text
          className={`node-label left${node.edge ? ' edge' : ''}`}
          x={labelX}
          y={node.edge && state.showDetail ? node.y - 3 : node.y}
        >
          {node.label}
        </text>
      )}

      {/* On an exposed machine the address is what you actually came to read,
          so it goes on the map rather than only in the inspector. When nothing
          knows it we show the MAC, which is what you pin an address against. */}
      {state.showLabel && state.showDetail && node.edge && (
        <text className="node-label left address" x={labelX} y={node.y + 9}>
          {edgeAddressText(guest)}
        </text>
      )}
    </g>
  );
}
